// Vue d'ensemble des finances (écran de référence S11) : répartition des montants de la période
// (confirmés, en caisse à vérifier, impayés échus, non échus), tuiles REC-02 à REC-08, caisses à
// vérifier, anomalies à instruire, dernières créances. Tout vient de l'API ; rien n'est inventé.
import { Filtres, LienBouton, PuceEtat, Registre, useFiltresPersistes, type ElementRegistre } from '@cc/ui';
import { FileSpreadsheet, FilePlus2 } from 'lucide-react';
import { useModulesActifs } from '../tableau/donnees';
import { calculerPeriode, type CodePeriode } from '../tableau/periode';
import { useDroitsRecettes, NavigationFinances } from './NavigationFinances';
import { EnTetePage } from '../../commun/EnTetePage';
import { date } from '../../commun/format';
import { francs, libelle, LIBELLES_ETAT_ANOMALIE, tonAnomalie, tonCreance } from './donnees';
import { useAnomalies, useCaisses, useCreances, useValeurRec, type PeriodeIndicateur } from './requetes';
import { TUILES_REC, TuileRecette } from './TuileRecette';
import styles from './recettes.module.css';

interface Segment {
  id: string;
  libelle: string;
  montant: number;
  classe: string;
  detail: string;
}

/** Répartition des montants : part de chaque segment sur les créances émises (REC-02). */
export function repartition(v: { emis: number | null; confirmes: number | null; caisse: number | null; echus: number | null; reste: number | null }): { segments: Segment[]; total: number } {
  const confirmes = v.confirmes ?? 0;
  const caisse = v.caisse ?? 0;
  const echus = v.echus ?? 0;
  const nonEchus = Math.max(0, (v.reste ?? 0) - echus);
  const total = Math.max(v.emis ?? 0, confirmes + caisse + echus + nonEchus);
  return {
    total,
    segments: [
      { id: 'confirmes', libelle: 'Confirmés', montant: confirmes, classe: styles.ok ?? '', detail: 'en ligne, banque, caisse vérifiée, nets des remboursements' },
      { id: 'caisse', libelle: 'En caisse, à vérifier', montant: caisse, classe: styles.accent ?? '', detail: 'reçus provisoires non vérifiés' },
      { id: 'echus', libelle: 'Impayés échus', montant: echus, classe: styles.retard ?? '', detail: 'relances à J+7 et J+30' },
      { id: 'non_echus', libelle: 'Non échus', montant: nonEchus, classe: styles.neutre ?? '', detail: 'reste à recouvrer, échéance à venir' },
    ],
  };
}

function Repartition({ periode, actif }: { periode: PeriodeIndicateur; actif: boolean }) {
  const emis = useValeurRec('REC-02', periode, actif);
  const confirmes = useValeurRec('REC-04', periode, actif);
  const caisse = useValeurRec('REC-03', periode, actif);
  const echus = useValeurRec('REC-07', periode, actif);
  const reste = useValeurRec('REC-05', periode, actif);
  const taux = useValeurRec('REC-06', periode, actif);
  const pret = [emis, confirmes, caisse, echus, reste].every((q) => q.data !== undefined);
  if (!pret) return null;
  const r = repartition({ emis: emis.data?.valeur ?? null, confirmes: confirmes.data?.valeur ?? null, caisse: caisse.data?.valeur ?? null, echus: echus.data?.valeur ?? null, reste: reste.data?.valeur ?? null });
  const part = (m: number) => (r.total > 0 ? `${((100 * m) / r.total).toFixed(1)}%` : '0%');
  const description = r.segments.map((s) => `${s.libelle} ${francs(s.montant)}`).join(', ');
  return (
    <section aria-labelledby="repartition-titre" className={styles.pile}>
      <div className={styles.entre}>
        <h2 id="repartition-titre">Encaissements de la période, sur {francs(emis.data?.valeur ?? 0)} émis</h2>
        <span className={styles.sous}>
          Taux de recouvrement des créances émises : <strong>{taux.data?.valeur === null || taux.data?.valeur === undefined ? 'non renseigné' : `${taux.data.valeur.toLocaleString('fr-FR')} %`}</strong>
        </span>
      </div>
      <div role="img" aria-label={`Répartition : ${description}`} className={styles.repartition}>
        {r.segments.map((s) => (
          <span key={s.id} className={s.classe} style={{ width: part(s.montant) }} />
        ))}
      </div>
      <dl className={styles.segments}>
        {r.segments.map((s) => (
          <div key={s.id}>
            <dt>
              <span className={`${styles.carre} ${s.classe}`} aria-hidden />
              {s.libelle}
            </dt>
            <dd className={styles.grand} data-testid={`segment-${s.id}`}>
              {francs(s.montant)}
            </dd>
            <dd className={styles.petit}>{s.detail}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function PageRecettes() {
  const droits = useDroitsRecettes();
  const modules = useModulesActifs();
  const actif = !modules || modules.has('M07');
  const [filtres, setFiltres] = useFiltresPersistes('recettes-periode', { periode: 'mois' });
  const code = (filtres.periode || 'mois') as CodePeriode;
  const p = calculerPeriode(code);
  const periode = { debut: p.debut, fin: p.fin };
  const caisses = useCaisses();
  const anomalies = useAnomalies({ etat: '', type: '', ouvertes: 'oui' });
  const creances = useCreances({ etat: '', echue: '', rubrique: '', nature: '' }, droits.voir);
  const aVerifier = (caisses.data ?? []).filter((k) => k.a_verifier > 0);
  const elementsAnomalies: ElementRegistre[] = (anomalies.data ?? []).slice(0, 6).map((a) => ({
    id: a.id,
    reference: date(a.cree_le),
    objet: a.libelle,
    sousTitre: `Objet : ${a.objet.type}`,
    etat: { ton: tonAnomalie(a.etat), libelle: libelle(LIBELLES_ETAT_ANOMALIE, a.etat) },
    href: `/recettes/anomalies/${a.id}`,
  }));
  const dernieres: ElementRegistre[] = (creances.data ?? []).slice(0, 6).map((c) => ({
    id: c.id,
    reference: c.numero,
    objet: `${c.redevable.nom}, ${c.rubrique.libelle}`,
    sousTitre: `Dû ${francs(c.montant_du)}, payé ${francs(c.montant_paye)}, reste ${francs(c.reste)}, échéance ${date(c.echeance)}`,
    etat: tonCreance(c),
    href: `/recettes/creances/${c.id}`,
  }));

  return (
    <>
      <EnTetePage
        etiquette="Finances"
        titre="Recettes"
        description={`${p.libelle} ; créances, encaissements, caisses et rapprochements ; montants en francs CFA.`}
        actions={
          <>
            {droits.modifier ? (
              <LienBouton href="/recettes/rapprochements" icone={FileSpreadsheet}>
                Importer un relevé
              </LienBouton>
            ) : null}
            {droits.creer ? (
              <LienBouton href="/recettes/creances?nouvelle=1" variante="principal" icone={FilePlus2}>
                Émettre une créance
              </LienBouton>
            ) : null}
          </>
        }
      />
      <NavigationFinances />
      <Filtres libelle="Période" groupes={[{ id: 'periode', libelle: 'Période', options: [{ valeur: 'mois', libelle: 'Mois' }, { valeur: 'trimestre', libelle: 'Trimestre' }, { valeur: 'exercice', libelle: 'Exercice' }] }]} valeurs={filtres} onChanger={setFiltres} />
      {droits.voir ? <Repartition periode={periode} actif={actif} /> : null}
      {droits.voir ? (
        <section aria-label="Indicateurs des recettes">
          <ul className={styles.tuiles}>
            {TUILES_REC.map((t) => (
              <li key={t.code}>
                <TuileRecette definition={t} periode={periode} actif={actif} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <div className={styles.deuxTiers}>
        {droits.caisseVoir ? (
          <Registre
            niveau={2}
            titre="Caisses à vérifier"
            compteur={caisses.data ? `${aVerifier.length} sur ${caisses.data.length}` : undefined}
            elements={aVerifier.map((k) => ({ id: k.id, reference: k.lieu, objet: k.libelle, sousTitre: `Régisseur : ${k.regisseur}, ${francs(k.a_verifier)} à vérifier`, etat: { ton: 'signer', libelle: 'À vérifier' }, href: `/recettes/caisses/${k.id}` }))}
            vide={<p className={styles.note}>{caisses.error ? 'Les caisses ne sont pas lisibles.' : 'Aucun reçu de caisse en attente de vérification.'}</p>}
          />
        ) : null}
        {droits.voir ? (
          <Registre
            niveau={2}
            titre="Anomalies à instruire"
            compteur={anomalies.data ? `${anomalies.data.length} ouverte${anomalies.data.length > 1 ? 's' : ''}` : undefined}
            elements={elementsAnomalies}
            vide={<p className={styles.note}>{anomalies.error ? 'Les anomalies ne sont pas lisibles.' : 'Aucune anomalie ouverte. Une anomalie signale un point à vérifier ; elle ne prononce rien.'}</p>}
          />
        ) : null}
      </div>
      {droits.voir ? (
        <Registre
          niveau={2}
          titre="Dernières créances"
          compteur={creances.data ? <LienBouton href="/recettes/creances" petit>Toutes les créances</LienBouton> : undefined}
          elements={dernieres}
          vide={<p className={styles.note}>{creances.error ? 'Les créances ne sont pas lisibles.' : 'Aucune créance émise.'}</p>}
        />
      ) : null}
      {!droits.voir && !droits.caisseVoir ? <PuceEtat ton="neutre" libelle="Aucun droit sur les recettes ni les caisses" /> : null}
      <p className={styles.note}>Les tarifs de présentation portent la mention « (fictif) » ; une quittance émise hors production porte la mention de son environnement.</p>
    </>
  );
}
